<template>
  <div class="content">
    <div class="content__state-duration-item">
      <div class="content__state-duration-item__status">
        <div class="content__state-duration-item__status__label label" data-test="alert_state_duration_item_status_txt">
          {{ $t('amd:alerts_configuration_type_status_label') }}
        </div>
        <le-select
          theme="theme-light"
          class="content__state-duration-item__status__dropdown"
          type="dropdown"
          ref="statusDropdown"
          :items="statuses"
          :placeholder="`${$t('amd:alerts_configuration_type_status_select_placeholder')}`"
          v-model="selectedStatus"
          @input="onStatusChange()"
          data-test="alert_state_duration_item_status_select"
        />
      </div>
      <div v-if="showStatusReasonSelect"
           class="content__state-duration-item__status-reason">
        <div class="content__state-duration-item__status-reason__label label" data-test="alert_state_duration_item_status_reason_txt">
          {{ $t('amd:alerts_configuration_type_status_reason_label') }}
        </div>
        <le-select
          theme="theme-light"
          class="content__state-duration-item__status-reason__dropdown"
          type="dropdown"
          ref="statusReasonDropdown"
          :items="getStatusReasonsDropdownItems"
          :placeholder="`${$t('amd:alerts_configuration_type_status_reason_select_placeholder')}`"
          v-model="selectedStatusReason"
          @input="onInputChange"
          data-test="alert_state_duration_item_status_reason_select"
        />
      </div>
      <div class="content__state-duration-item__type">
        <div class="content__state-duration-item__type__label label" data-test="alert_state_duration_item_config_type_txt">
          {{ $t('amd:alerts_configuration_type_label') }}
        </div>
        <le-select
          theme="theme-light"
          class="content__state-duration-item__type__dropdown"
          type="dropdown"
          ref="conditionDropdown"
          :items="conditionTypes"
          :placeholder="`${$t('amd:alerts_configuration_type_combo_placeholder')}`"
          v-model="selectedConditionType"
          @input="onInputChange()"
          data-test="alert_state_duration_item_config_type_select"
        />
      </div>
      <div v-if="isThresholdInputShown"
           class="content__state-duration-item__time-value">
        <div class="content__state-duration-item__time-value__from">
          <div class="content__state-duration-item__time-value__from__label label" data-test="alert_state_duration_item_threshold_from_txt">
            {{ $t('amd:alerts_configuration_threshold_label_from') }}
          </div>
          <duration ref="durationValueComp"
                    v-model="selectedDurationValueFrom"
                    :validation="validation"
                    @input="onInputChange()"
                    data-test="alert_state_duration_item_main"
                    >
          </duration>
        </div>
      </div>
      <le-button class="content__state-duration-item__reset"
                 variant="link"
                 :label="$t('amd:alerts_configuration_threshold_clear')"
                 :disabled="isRowEmpty"
                 @click="clearDurationItem()"
                 data-test="alert_state_duration_item_clear_btn"
                 >
      </le-button>
      <general-icon v-if="index !== 0"
                    class="content__state-duration-item__remove"
                    icon="close-icon"
                    tabindex="0"
                    role="button"
                    :aria-label="$t('amd:alerts_configuration_threshold_remove')"
                    @keydown.space.enter.prevent="removeDurationItem()"
                    @click.native="removeDurationItem()"
                    data-test="alert_state_duration_item_delete_btn"
      />
    </div>
    <div class="content__error-message" data-test="alert_state_duration_item_error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { filter, map, get, isEmpty, each, find } from 'lodash';
import { leSelect, leButton } from 'lp-vue-components';
import GeneralIcon from '@components/common/icons/general-icon';
import duration from '@components/common/duration';
import {
  AGENT_STATUSES,
  ALERT_CONDITION_TYPES,
} from '@config/Consts';

import { WIDGET_PATHS } from '@core/store';

export default {
  name: 'alert-state-duration-item',
  components: {
    leSelect,
    duration,
    GeneralIcon,
    leButton,
  },
  props: {
    index: {
      type: Number,
      required: true,
    },
    validation: {
      type: Object,
      default: undefined,
      required: true,
    },
    durationItem: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      id: null,
      statuses: this.getStatusDropdownItems(),
      conditionTypes: this.getConditionTypeDropdownItems(),
      selectedStatus: null,
      selectedStatusReason: null,
      selectedConditionType: null,
      selectedDurationValueFrom: null,
      errorMessage: '',
      storePath: WIDGET_PATHS.AGENT_LIST,
    };
  },
  mounted() {
    this.$nextTick(() => {
      if (this.selectedStatusReason) {
        const currentReason = find(this.getStatusReasons, reason =>
          (reason.id === this.selectedStatusReason.id));
        if (!currentReason) {
          this.setInputErrors([this.$refs.statusReasonDropdown], true,
            this.$t('amd:alerts_configuration_agent_status_reason_non_existent'));
        }
      }
    });
  },
  watch: {
    durationItem: {
      handler() {
        if (this.durationItem) {
          this.id = get(this.durationItem, 'id');
          this.selectedStatus = get(this.durationItem, 'status', null);
          this.selectedStatusReason = get(this.durationItem, 'statusReason', null);
          this.selectedConditionType = get(this.durationItem, 'conditionType', null);
          this.selectedDurationValueFrom = get(this.durationItem, 'durationValueFrom', null);
        }
      },
      immediate: true,
    },
  },
  computed: {
    getStatusReasons() {
      return this.$store.getters[`${this.storePath}/getStatusReasons`];
    },
    getStatusReasonsDropdownItems() {
      return map(this.getStatusReasons, item => ({ id: item.id, value: item.text }));
    },
    showStatusReasonSelect() {
      return (this.selectedStatus && this.selectedStatus.id === AGENT_STATUSES.AWAY
        && (this.getStatusReasons.length > 0 || this.selectedStatusReason));
    },
    isRowEmpty() {
      return isEmpty(this.selectedStatus) &&
        isEmpty(this.selectedStatusReason) &&
        isEmpty(this.selectedConditionType) &&
        isEmpty(this.selectedDurationValueFrom);
    },
    isThresholdInputShown() {
      return !isEmpty(this.selectedConditionType);
    },
  },
  methods: {
    getStatusDropdownItems() {
      return map(AGENT_STATUSES, item => ({ id: item, value: item }))
        .filter(status => status.id !== AGENT_STATUSES.OFFLINE);
    },
    getConditionTypeDropdownItems() {
      return map(filter(ALERT_CONDITION_TYPES,
        item => (item !== ALERT_CONDITION_TYPES.BETWEEN)), item => ({ id: item, value: item }));
    },
    onInputChange() {
      this.$emit('onInputChange');
    },
    onStatusChange() {
      this.onInputChange();
      if (this.selectedStatus !== AGENT_STATUSES.AWAY) {
        this.selectedStatusReason = null;
      }
    },
    removeDurationItem() {
      this.resetInputs();
      this.$emit('removeItemClicked', this.index);
    },
    clearDurationItem() {
      this.resetInputs();
      this.$emit('clearItemClicked', this.index);
    },
    resetInputs() {
      this.clearError();
      this.selectedStatus = null;
      this.selectedStatusReason = null;
      this.selectedConditionType = null;
      this.selectedDurationValueFrom = null;
    },
    setInputErrors(controls, showError, errorMessage) {
      each(controls, ref => (ref && (showError ? ref.$el.classList.add('error-border') : ref.$el.classList.remove('error-border'))));
      this.errorMessage = errorMessage;
    },
    clearError() {
      const controls = [
        this.$refs.statusDropdown,
        this.$refs.statusReasonDropdown,
        this.$refs.conditionDropdown,
      ];
      this.setInputErrors(controls, false, '');
    },
    validate() {
      if (isEmpty(this.selectedStatus) && isEmpty(this.selectedConditionType)) {
        return true;
      }

      const emptyInputsArray = this.getEmptyChildrenRefs();
      if (emptyInputsArray.length !== 0) {
        this.setInputErrors(emptyInputsArray, true,
          this.$t('amd:alerts_configuration_threshold_required_validation'));
        return false;
      }

      if (this.isThresholdInputShown) {
        const validationResult = this.$refs.durationValueComp.validate();
        const error = find(validationResult, item => !item.isValid);
        if (error) {
          this.errorMessage = error.message;
          return false;
        }
      }
      return true;
    },
    getChildData() {
      return this.isRowEmpty ? null : {
        id: this.id,
        status: this.selectedStatus,
        statusReason: this.selectedStatusReason,
        conditionType: this.selectedConditionType,
        durationValueFrom: this.selectedDurationValueFrom,
      };
    },
    getEmptyChildrenRefs() {
      const emptyRefs = [];
      if (isEmpty(this.selectedStatus)) {
        emptyRefs.push(this.$refs.statusDropdown);
      }
      if (this.showStatusReasonSelect && isEmpty(this.selectedStatusReason)) {
        emptyRefs.push(this.$refs.statusReasonDropdown);
      }
      if (isEmpty(this.selectedConditionType)) {
        emptyRefs.push(this.$refs.conditionDropdown);
      }
      return emptyRefs;
    },
  },
};
</script>

<style lang="scss" scoped>
.content {
  &__state-duration-item {
    display: flex;
    align-items: center;
    margin-bottom: 10px;

    .label {
      margin-bottom: 5px;
    }
    &__status,
    &__status-reason,
    &__type {
      width: 110px;
      margin-right: 10px;
    }
    &__time-value {
      display: flex;
      align-items: center;
      &__from{
        position: relative;
      }
    }
    &__reset {
      margin: 18px 0 0 10px;
    }
    &__remove {
      margin: 20px 0 0 10px;
    }
    .error-border {
      @include themify {
        border: 1px solid getColor('alertValidationError', 'amd');
      }
    }
  }
  &__error-message {
    margin-bottom: 10px;
    min-height: 13px;
    @include themify {
      color: getColor('alertValidationError', 'amd');
    }
  }
}
</style>
